import {
  useCallback,
  useEffect,
  useRef,
  useState,
  forwardRef,
} from "react";
import {
  Heart,
  Hand,
  Star,
  Volume2,
  VolumeX,
  Sparkles,
  Share2,
  Trophy,
  Instagram,
} from "lucide-react";
import { getBelt, BELTS } from "./belts";
import { renderShareCard, dataURLToFile } from "./renderShareCard";
import {
  unlockAudio,
  playHit,
  playError,
  playEnd,
  playCountBeep,
} from "./sound";

const GAME_MS = 45000;
const SCHNUPPER_URL = "https://kennenlerntag.kampfkunst-herz.de/";
const NAME_KEY = "zdp-name";
const LEVEL_KEY = "zdp-level";
const LB_KEY = "zdp-leaderboard";

const LEVELS = {
  leicht: {
    key: "leicht",
    label: "Leicht",
    hint: "Ruhiges Tempo",
    spawnStart: 1250,
    spawnEnd: 850,
    lifeStart: 1800,
    lifeEnd: 1350,
    redChance: 0.15,
    doubleAfter: Infinity,
  },
  mittel: {
    key: "mittel",
    label: "Mittel",
    hint: "Flottes Tempo",
    spawnStart: 950,
    spawnEnd: 560,
    lifeStart: 1300,
    lifeEnd: 820,
    redChance: 0.25,
    doubleAfter: 20000,
  },
  schwer: {
    key: "schwer",
    label: "Schwer",
    hint: "Volle Power",
    spawnStart: 720,
    spawnEnd: 400,
    lifeStart: 1050,
    lifeEnd: 650,
    redChance: 0.33,
    doubleAfter: 12000,
  },
};
const LEVEL_ORDER = ["leicht", "mittel", "schwer"];
const BELT_BY_KEY = BELTS.reduce((m, b) => {
  m[b.key] = b;
  return m;
}, {});

function loadLeaderboard() {
  try {
    const raw = JSON.parse(localStorage.getItem(LB_KEY) || "[]");
    if (Array.isArray(raw)) return raw;
  } catch (e) {
    console.warn("Bestenliste konnte nicht gelesen werden:", e);
  }
  return [];
}

function freshPads() {
  return Array.from({ length: 9 }, () => ({
    status: "idle", // idle | gold | red
    fx: null, // hit | wrong | miss
    fxUntil: 0,
    expiresAt: 0,
    sparkId: 0,
  }));
}

function initGame() {
  return {
    phase: "start", // start | countdown | playing | end
    pads: freshPads(),
    score: 0,
    hearts: 3,
    streak: 0,
    timeLeftMs: GAME_MS,
    endAt: 0,
    nextSpawnAt: 0,
    countdownEndAt: 0,
    countdownNum: 3,
    lastCountShown: 4,
    shakeUntil: 0,
    lastResult: null,
    level: "mittel",
    cfg: LEVELS.mittel,
    name: "",
  };
}

export default function FokusPratzen() {
  const g = useRef(initGame());
  const [, setTick] = useState(0);
  const render = useCallback(() => setTick((t) => (t + 1) % 1000000), []);
  const intervalRef = useRef(null);
  const soundRef = useRef(false);
  const [soundOn, setSoundOn] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [name, setName] = useState("");
  const [level, setLevel] = useState("mittel");
  const [sharing, setSharing] = useState(false);
  const [shareMsg, setShareMsg] = useState("");
  const [shareImg, setShareImg] = useState(null);
  const [shareData, setShareData] = useState(null);
  const shareCardRef = useRef(null);

  useEffect(() => {
    setLeaderboard(loadLeaderboard());
    const savedName = localStorage.getItem(NAME_KEY) || "";
    setName(savedName);
    const savedLevel = localStorage.getItem(LEVEL_KEY);
    if (savedLevel && LEVELS[savedLevel]) setLevel(savedLevel);
  }, []);

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const sfx = (fn) => {
    if (soundRef.current) fn();
  };

  const stopLoop = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const endGame = useCallback(() => {
    const G = g.current;
    stopLoop();
    G.phase = "end";
    const belt = getBelt(G.score);
    const prevLb = loadLeaderboard();
    const prevBest = prevLb.length ? prevLb[0].score : 0;
    const entry = {
      name: G.name || "Gast",
      score: G.score,
      level: G.level,
      belt: belt.key,
      date: Date.now(),
    };
    const nextLb = [...prevLb, entry]
      .sort((a, b) => b.score - a.score)
      .slice(0, 10);
    try {
      localStorage.setItem(LB_KEY, JSON.stringify(nextLb));
    } catch (e) {
      console.warn("Bestenliste konnte nicht gespeichert werden:", e);
    }
    const position = nextLb.indexOf(entry);
    setLeaderboard(nextLb);
    G.lastResult = {
      score: G.score,
      belt,
      level: G.level,
      levelLabel: LEVELS[G.level] ? LEVELS[G.level].label : "",
      name: entry.name,
      isBest: G.score > prevBest && G.score > 0,
      position: position >= 0 ? position + 1 : null,
      total: nextLb.length,
    };
    G.pads = freshPads();
    sfx(playEnd);
    render();
    setShareData(null);
    setShareImg(null);
    const forShare = G.lastResult;
    renderShareCard(forShare)
      .then((url) =>
        setShareData({
          dataUrl: url,
          file: dataURLToFile(url, "zieh-durch-drueck-drauf.png"),
        }),
      )
      .catch(() => {});
  }, [render]);

  const spawnOne = (ts, p) => {
    const G = g.current;
    const cfg = G.cfg;
    const free = [];
    for (let i = 0; i < 9; i++) {
      const pad = G.pads[i];
      const busy = pad.status !== "idle" || (pad.fx && ts < pad.fxUntil);
      if (!busy) free.push(i);
    }
    if (free.length === 0) return;
    const i = free[Math.floor(Math.random() * free.length)];
    const pad = G.pads[i];
    const isRed = Math.random() < cfg.redChance;
    const span = cfg.lifeStart - cfg.lifeEnd;
    const life = (isRed ? cfg.lifeStart + 200 : cfg.lifeStart) - span * p;
    pad.status = isRed ? "red" : "gold";
    pad.expiresAt = ts + life;
    pad.fx = null;
  };

  const step = useCallback(() => {
    const G = g.current;
    const ts = performance.now();

    if (G.phase === "countdown") {
      const rem = G.countdownEndAt - ts;
      if (rem <= 0) {
        G.phase = "playing";
        G.endAt = ts + GAME_MS;
        G.nextSpawnAt = ts + 650;
        G.timeLeftMs = GAME_MS;
      } else {
        const num = Math.ceil(rem / 1000);
        if (num !== G.countdownNum) {
          G.countdownNum = num;
          if (num <= 3) sfx(playCountBeep);
        }
      }
      render();
      return;
    }

    if (G.phase !== "playing") return;

    G.timeLeftMs = Math.max(0, G.endAt - ts);
    const elapsed = GAME_MS - G.timeLeftMs;
    const p = Math.min(1, elapsed / GAME_MS);

    // Ablaufen & Effekte
    for (let i = 0; i < 9; i++) {
      const pad = G.pads[i];
      if (pad.status !== "idle" && ts >= pad.expiresAt) {
        if (pad.status === "gold") {
          pad.status = "idle";
          pad.fx = "miss";
          pad.fxUntil = ts + 400;
          G.hearts -= 1;
          G.streak = 0;
          G.shakeUntil = ts + 300;
          sfx(playError);
        } else {
          pad.status = "idle";
          pad.fx = null;
        }
      }
      if (pad.fx && ts >= pad.fxUntil) pad.fx = null;
    }

    if (G.hearts <= 0) {
      endGame();
      return;
    }
    if (G.timeLeftMs <= 0) {
      endGame();
      return;
    }

    // Nachschub
    if (ts >= G.nextSpawnAt) {
      const maxActive = elapsed >= G.cfg.doubleAfter ? 2 : 1;
      const activeCount = G.pads.filter(
        (pd) => pd.status === "gold" || pd.status === "red",
      ).length;
      if (activeCount < maxActive) spawnOne(ts, p);
      const interval =
        G.cfg.spawnStart - (G.cfg.spawnStart - G.cfg.spawnEnd) * p;
      G.nextSpawnAt = ts + interval;
    }

    render();
  }, [endGame, render]);

  const startGame = useCallback(() => {
    const chosen = LEVELS[level] || LEVELS.mittel;
    const cleanName = (name || "").trim().slice(0, 14) || "Gast";
    try {
      localStorage.setItem(NAME_KEY, cleanName);
      localStorage.setItem(LEVEL_KEY, chosen.key);
    } catch (e) {
      console.warn("Einstellungen konnten nicht gespeichert werden:", e);
    }
    g.current = initGame();
    const ts = performance.now();
    g.current.phase = "countdown";
    g.current.countdownEndAt = ts + 3000;
    g.current.countdownNum = 3;
    g.current.level = chosen.key;
    g.current.cfg = chosen;
    g.current.name = cleanName;
    setShareMsg("");
    stopLoop();
    intervalRef.current = setInterval(step, 50);
    render();
    if (soundRef.current) unlockAudio();
  }, [render, step, level, name]);

  const tapPad = useCallback(
    (i) => {
      const G = g.current;
      if (G.phase !== "playing") return;
      const ts = performance.now();
      const pad = G.pads[i];
      if (pad.status === "gold") {
        G.streak += 1;
        const mult = G.streak >= 5 ? 2 : 1;
        G.score += 10 * mult;
        pad.status = "idle";
        pad.fx = "hit";
        pad.fxUntil = ts + 450;
        pad.sparkId = (pad.sparkId || 0) + 1;
        sfx(playHit);
      } else if (pad.status === "red") {
        G.hearts -= 1;
        G.streak = 0;
        pad.status = "idle";
        pad.fx = "wrong";
        pad.fxUntil = ts + 450;
        G.shakeUntil = ts + 300;
        sfx(playError);
      } else {
        return;
      }
      render();
    },
    [render],
  );

  const toggleSound = () => {
    const next = !soundRef.current;
    soundRef.current = next;
    setSoundOn(next);
    if (next) unlockAudio();
  };

  const shareGeneratedFile = useCallback(async (data) => {
    if (
      navigator.share &&
      navigator.canShare &&
      data.file &&
      navigator.canShare({ files: [data.file] })
    ) {
      try {
        await navigator.share({
          files: [data.file],
          title: "Zieh durch! Drück drauf!",
          text: "Mein Rekord bei „Zieh durch! Drück drauf!“ von KAMPFKUNST HERZ®. Spielst du mit?",
        });
        return true;
      } catch (e) {
        if (e && e.name === "AbortError") return true;
      }
    }
    setShareImg(data.dataUrl);
    return false;
  }, []);

  const handleShare = useCallback(async () => {
    if (sharing) return;
    setShareMsg("");
    if (shareData) {
      await shareGeneratedFile(shareData);
      return;
    }
    setSharing(true);
    try {
      const url = await renderShareCard(g.current.lastResult);
      const data = {
        dataUrl: url,
        file: dataURLToFile(url, "zieh-durch-drueck-drauf.png"),
      };
      setShareData(data);
      await shareGeneratedFile(data);
    } catch (e) {
      console.warn("Rekord-Bild konnte nicht erstellt werden:", e);
      setShareMsg("Klappt nicht? Mach einfach einen Screenshot!");
    } finally {
      setSharing(false);
    }
  }, [sharing, shareData, shareGeneratedFile]);

  const shareImage = useCallback(async () => {
    if (!shareImg) return;
    const fileName = "zieh-durch-drueck-drauf.png";
    try {
      const res = await fetch(shareImg);
      const blob = await res.blob();
      const file = new File([blob], fileName, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "Zieh durch! Drück drauf!",
          text: "Mein Rekord bei „Zieh durch! Drück drauf!“ von KAMPFKUNST HERZ®. Spielst du mit?",
        });
        return;
      }
    } catch (e) {
      console.warn("Teilen fehlgeschlagen, nutze Download:", e);
    }
    const a = document.createElement("a");
    a.href = shareImg;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, [shareImg]);

  const closeShare = useCallback(() => setShareImg(null), []);

  const G = g.current;
  const now = performance.now();
  const shaking = G.shakeUntil > now;
  const multiplierActive = G.streak >= 5;
  const seconds = Math.ceil(G.timeLeftMs / 1000);

  return (
    <div className="kh-app" data-testid="kh-app">
      <div className="relative w-full max-w-[460px] px-5 pt-4 pb-6 flex flex-col min-h-[100dvh]">
        <BrandBar
          soundOn={soundOn}
          onToggleSound={toggleSound}
          phase={G.phase}
        />

        {G.phase === "start" && (
          <StartScreen
            name={name}
            setName={setName}
            level={level}
            setLevel={setLevel}
            leaderboard={leaderboard}
            onStart={startGame}
          />
        )}

        {(G.phase === "countdown" || G.phase === "playing") && (
          <div className={shaking ? "kh-shake" : ""}>
            <Hud
              hearts={G.hearts}
              score={G.score}
              seconds={seconds}
              streak={G.streak}
              multiplierActive={multiplierActive}
              phase={G.phase}
            />
            <div className="relative mt-4">
              <Grid pads={G.pads} onTap={tapPad} disabled={G.phase !== "playing"} />
              {G.phase === "countdown" && (
                <CountdownOverlay num={G.countdownNum} />
              )}
            </div>
            <p className="text-center text-[13px] text-[var(--grau)] mt-5 font-medium leading-relaxed">
              Triff die leuchtenden Pratzen.
              <br />
              Lass die roten stehen!
            </p>
          </div>
        )}

        {G.phase === "end" && G.lastResult && (
          <EndScreen
            result={G.lastResult}
            leaderboard={leaderboard}
            onRestart={startGame}
            onShare={handleShare}
            sharing={sharing}
            shareMsg={shareMsg}
          />
        )}

        <Footer />
      </div>

      {shareImg && (
        <div
          data-testid="share-modal"
          onClick={closeShare}
          className="fixed inset-0 z-50 flex items-center justify-center p-5"
          style={{
            background: "rgba(59,10,18,0.55)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-[var(--card)] rounded-3xl p-4 w-full max-w-[340px] fade-up"
          >
            <img
              data-testid="share-image"
              src={shareImg}
              alt="Dein Rekord"
              className="w-full rounded-2xl border border-[var(--line)]"
            />
            <p className="text-[12px] text-[var(--grau)] text-center mt-3 font-medium leading-snug">
              Speichere das Bild und teile es in deiner Story.
              <br />
              Vergiss nicht uns auf Instagram zu markieren!
            </p>
            <div className="flex flex-col gap-2 mt-3">
              <button
                type="button"
                onClick={shareImage}
                data-testid="share-native-button"
                className="kh-btn kh-btn-primary rounded-full py-3 w-full"
              >
                Teilen oder speichern
              </button>
              <button
                type="button"
                onClick={closeShare}
                data-testid="share-close-button"
                className="kh-btn kh-btn-ghost rounded-full py-3 w-full"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Teilkomponenten ---------------- */

function BrandBar({ soundOn, onToggleSound }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2.5" data-testid="brand">
        <img
          src="/logo.png"
          alt="KAMPFKUNST HERZ Logo"
          className="h-9 w-9 object-contain"
        />
        <div className="leading-tight">
          <div
            className="kh-display text-[13px] text-[var(--ink)]"
            style={{ letterSpacing: "0.06em" }}
          >
            KAMPFKUNST HERZ
            <span className="align-super text-[8px] ml-0.5">®</span>
          </div>
          <div className="text-[10px] text-[var(--grau)] font-semibold tracking-wide">
            Die Lebensschule für Kinder
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={onToggleSound}
        data-testid="sound-toggle"
        aria-label={soundOn ? "Ton aus" : "Ton an"}
        className="h-9 w-9 rounded-full bg-[var(--card)] border border-[var(--line)] flex items-center justify-center text-[var(--grau)] active:scale-95 transition"
      >
        {soundOn ? (
          <Volume2 className="h-4 w-4 text-[var(--herzrot)]" />
        ) : (
          <VolumeX className="h-4 w-4" />
        )}
      </button>
    </div>
  );
}

function StartScreen({ name, setName, level, setLevel, leaderboard, onStart }) {
  const canStart = (name || "").trim().length > 0;
  return (
    <div className="flex-1 flex flex-col items-center text-center fade-up pt-2">
      <div className="kh-eyebrow mb-3">Kleine Konzentrationsübung</div>
      <h1
        className="kh-display kh-headline text-4xl sm:text-5xl mb-3 leading-[0.92]"
        data-testid="game-title"
      >
        Zieh durch!
        <br />
        Drück drauf!
      </h1>
      <p className="text-[14px] leading-relaxed text-[var(--grau)] max-w-[320px] mb-5 font-medium">
        Triff die leuchtenden Pratzen.
        <br />
        Lass die roten stehen!
      </p>

      <div className="flex items-center justify-center gap-4 mb-6 flex-wrap">
        <div className="flex items-center gap-2">
          <MiniPad kind="gold" />
          <span className="text-[var(--grau)] text-xs font-semibold">
            antippen
          </span>
        </div>
        <div className="flex items-center gap-2">
          <MiniPad kind="red" />
          <span className="text-[var(--grau)] text-xs font-semibold">
            stehen lassen
          </span>
        </div>
      </div>

      <div className="w-full max-w-[320px] text-left mb-5">
        <label className="kh-eyebrow block mb-2" htmlFor="vorname">
          Dein Vorname
        </label>
        <input
          id="vorname"
          data-testid="name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={14}
          placeholder="z. B. Kostjantyn"
          className="w-full rounded-2xl border-[1.5px] border-[var(--line)] bg-[var(--card)] px-4 py-3 text-[16px] font-semibold text-[var(--ink)] outline-none focus:border-[var(--herzrot)] transition"
        />
      </div>

      <div className="w-full max-w-[320px] text-left mb-6">
        <span className="kh-eyebrow block mb-2">Schwierigkeit</span>
        <LevelPicker level={level} setLevel={setLevel} />
      </div>

      <button
        type="button"
        onClick={onStart}
        disabled={!canStart}
        data-testid="start-button"
        className={
          "kh-btn kh-btn-primary text-lg px-10 py-4 rounded-full w-full max-w-[320px] flex items-center justify-center gap-2 " +
          (canStart ? "" : "opacity-40 pointer-events-none")
        }
      >
        Los geht's
        <Sparkles className="h-5 w-5" />
      </button>
      {!canStart && (
        <div className="mt-2 text-[12px] text-[var(--grau)] font-medium">
          Gib zuerst deinen Vornamen ein.
        </div>
      )}

      <Leaderboard
        entries={leaderboard}
        className="mt-8 w-full max-w-[320px]"
      />
    </div>
  );
}

function LevelPicker({ level, setLevel }) {
  return (
    <div className="grid grid-cols-3 gap-2" data-testid="level-picker">
      {LEVEL_ORDER.map((key) => {
        const cfg = LEVELS[key];
        const active = level === key;
        return (
          <button
            key={key}
            type="button"
            onClick={() => setLevel(key)}
            data-testid={`level-${key}`}
            aria-pressed={active}
            className={
              "kh-btn rounded-2xl px-2 py-3 border-[1.5px] flex flex-col items-center gap-0.5 transition " +
              (active
                ? "bg-[var(--herzrot)] text-white border-[var(--herzrot)]"
                : "bg-[var(--card)] text-[var(--ink)] border-[var(--line)]")
            }
          >
            <span className="kh-display text-base leading-none">
              {cfg.label}
            </span>
            <span
              className={
                "text-[10px] font-semibold " +
                (active ? "text-white/80" : "text-[var(--grau)]")
              }
            >
              {cfg.hint}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function Leaderboard({ entries, className = "", highlightIndex = -1 }) {
  return (
    <div className={className} data-testid="leaderboard">
      <div className="kh-eyebrow flex items-center justify-center gap-1.5 mb-3">
        <Trophy className="h-3.5 w-3.5" /> Bestenliste
      </div>
      {!entries || entries.length === 0 ? (
        <div className="text-[13px] text-[var(--grau)] font-medium text-center py-3">
          Noch keine Einträge. Sei die oder der Erste!
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          {entries.map((e, i) => {
            const belt = BELT_BY_KEY[e.belt];
            const highlight = i === highlightIndex;
            return (
              <div
                key={`${e.name}-${e.date}-${i}`}
                data-testid={`leaderboard-row-${i}`}
                className={
                  "flex items-center gap-3 rounded-xl px-3 py-2 border " +
                  (highlight
                    ? "border-[var(--herzrot)] bg-[var(--blush)]"
                    : "border-[var(--line)] bg-[var(--card)]")
                }
              >
                <span className="kh-display text-sm w-5 text-[var(--grau)]">
                  {i + 1}
                </span>
                <span
                  className="h-3.5 w-3.5 rounded-full shrink-0"
                  style={{
                    background: belt ? belt.color : "var(--line)",
                    boxShadow: belt ? `inset 0 0 0 2px ${belt.ring}` : "none",
                  }}
                  title={belt ? `Gürtel ${belt.label}` : ""}
                />
                <span className="flex-1 text-left text-[14px] font-bold text-[var(--ink)] truncate">
                  {e.name}
                </span>
                <span className="text-[10px] font-semibold text-[var(--grau)] uppercase tracking-wide">
                  {LEVELS[e.level] ? LEVELS[e.level].label : ""}
                </span>
                <span className="kh-display text-base text-[var(--herzrot)] tabular-nums w-12 text-right">
                  {e.score}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MiniPad({ kind }) {
  if (kind === "gold") {
    return (
      <div
        className="h-11 w-11 rounded-full flex items-center justify-center"
        style={{
          background: "#fff",
          boxShadow:
            "inset 0 0 0 4px #ffd66b, 0 0 14px 3px rgba(255,199,61,0.7)",
        }}
      >
        <Star className="h-4 w-4 text-[#ffc73d] fill-[#ffc73d]" />
      </div>
    );
  }
  return (
    <div
      className="h-11 w-11 rounded-full flex items-center justify-center text-white"
      style={{
        background: "var(--herzrot)",
        boxShadow: "inset 0 0 0 4px rgba(255,255,255,0.35)",
      }}
    >
      <Hand className="h-5 w-5" />
    </div>
  );
}

function Hud({ hearts, score, seconds, streak, multiplierActive }) {
  return (
    <div className="mt-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5" data-testid="hearts">
          {[0, 1, 2].map((i) => {
            const filled = i < hearts;
            return (
              <Heart
                key={i}
                className={
                  "h-6 w-6 transition-all " +
                  (filled ? "heart-pop" : "opacity-30")
                }
                style={{
                  color: "var(--herzrot)",
                  fill: filled ? "var(--herzrot)" : "transparent",
                }}
              />
            );
          })}
        </div>
        <div
          className="kh-display text-3xl tabular-nums text-[var(--ink)]"
          data-testid="timer"
        >
          {seconds}
          <span className="text-sm text-[var(--grau)] ml-1">s</span>
        </div>
      </div>

      <div className="flex items-center justify-between mt-2">
        <div className="flex items-baseline gap-2">
          <span className="kh-eyebrow">Punkte</span>
          <span
            className="kh-display text-3xl tabular-nums text-[var(--herzrot)]"
            data-testid="score"
          >
            {score}
          </span>
        </div>
        <ComboBadge streak={streak} active={multiplierActive} />
      </div>
    </div>
  );
}

function ComboBadge({ streak, active }) {
  if (streak < 2) return <div className="h-7" data-testid="combo" />;
  return (
    <div
      data-testid="combo"
      className={
        "kh-display text-sm px-3 py-1 rounded-full flex items-center gap-1 " +
        (active ? "text-white" : "text-[var(--ink)]")
      }
      style={{
        background: active ? "var(--herzrot)" : "var(--creme)",
        boxShadow: active ? "0 6px 16px -6px rgba(227,6,32,0.7)" : "none",
      }}
    >
      {active ? "×2 Combo" : `${streak}er Serie`}
    </div>
  );
}

function Grid({ pads, onTap, disabled }) {
  return (
    <div className="grid grid-cols-3 gap-3.5" data-testid="grid">
      {pads.map((pad, i) => (
        <Pad key={i} pad={pad} index={i} onTap={onTap} disabled={disabled} />
      ))}
    </div>
  );
}

function Pad({ pad, index, onTap, disabled }) {
  const cls =
    "pad " +
    (pad.status === "gold"
      ? "pad-gold"
      : pad.status === "red"
        ? "pad-red"
        : "pad-idle") +
    (pad.fx === "hit"
      ? " pad-fx-hit"
      : pad.fx === "wrong"
        ? " pad-fx-wrong"
        : pad.fx === "miss"
          ? " pad-fx-miss"
          : "");

  const handle = () => {
    if (!disabled) onTap(index);
  };

  return (
    <button
      type="button"
      className={cls}
      onPointerDown={handle}
      data-testid={`pad-${index}`}
      data-status={pad.status}
      aria-label={`Pratze ${index + 1}`}
    >
      {pad.status === "gold" && (
        <Star className="h-7 w-7 text-[#ffc73d] fill-[#ffc73d] opacity-90" />
      )}
      {pad.status === "red" && <Hand className="h-8 w-8" />}
      {pad.fx === "hit" && <Sparkle key={pad.sparkId} />}
    </button>
  );
}

function Sparkle() {
  const bits = [
    { tx: "-26px", ty: "-24px", d: 0 },
    { tx: "26px", ty: "-22px", d: 0.04 },
    { tx: "-22px", ty: "24px", d: 0.06 },
    { tx: "24px", ty: "26px", d: 0.02 },
    { tx: "0px", ty: "-32px", d: 0.08 },
  ];
  return (
    <>
      {bits.map((b, i) => (
        <Star
          key={i}
          className="spark spark-anim h-4 w-4 fill-[#ffc73d]"
          style={{ "--tx": b.tx, "--ty": b.ty, animationDelay: `${b.d}s` }}
        />
      ))}
    </>
  );
}

function CountdownOverlay({ num }) {
  const label = num > 3 ? 3 : num < 1 ? "Los!" : num;
  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
      <div
        key={num}
        className="kh-display kh-headline count-pop text-[8rem]"
        data-testid="countdown"
      >
        {label}
      </div>
    </div>
  );
}

function EndScreen({ result, leaderboard, onRestart, onShare, sharing, shareMsg }) {
  const { score, belt, isBest, position, total, level, name } = result;
  const highlightIndex = position ? position - 1 : -1;
  return (
    <div
      className="flex-1 flex flex-col items-center text-center fade-up pt-2"
      data-testid="end-screen"
    >
      <div className="kh-eyebrow mb-2">
        {name ? `Stark, ${name}!` : "Geschafft"}
      </div>

      <BeltBadge belt={belt} />

      <div className="mt-4 mb-1">
        <div className="kh-eyebrow mb-1">Dein Ergebnis</div>
        <div
          className="kh-display text-6xl text-[var(--herzrot)]"
          data-testid="final-score"
        >
          {score}
        </div>
      </div>

      {isBest && (
        <div
          className="text-[13px] font-bold text-[var(--herzrot)] flex items-center gap-1 mt-1"
          data-testid="new-highscore"
        >
          <Sparkles className="h-4 w-4" /> Neuer Rekord!
        </div>
      )}
      {!isBest && position && (
        <div
          className="text-[13px] text-[var(--grau)] font-semibold mt-1"
          data-testid="rank-line"
        >
          Platz {position} von {total} · {LEVELS[level] ? LEVELS[level].label : ""}
        </div>
      )}

      <p className="text-[14px] leading-relaxed text-[var(--ink)] max-w-[320px] mt-4 mb-5 font-medium">
        {belt.message}
      </p>

      <div className="w-full max-w-[320px] flex flex-col gap-3">
        <div
          className="rounded-2xl border border-[var(--line)] bg-[var(--blush)] px-4 py-3 flex items-start gap-2.5 text-left"
          data-testid="share-cta"
        >
          <Instagram className="h-5 w-5 text-[var(--herzrot)] shrink-0 mt-0.5" />
          <p className="text-[13px] leading-snug text-[var(--ink)] font-semibold">
            Markiere uns auf Instagram und teile deinen persönlichen Rekord in
            deinen Stories.
          </p>
        </div>
        <button
          type="button"
          onClick={onShare}
          disabled={sharing}
          data-testid="share-button"
          className="kh-btn kh-btn-primary text-lg px-8 py-4 rounded-full w-full flex items-center justify-center gap-2"
        >
          <Share2 className="h-5 w-5" />
          {sharing ? "Rekord wird erstellt …" : "Rekord in Story teilen"}
        </button>
        <div className="text-[11px] text-[var(--grau)] text-center -mt-1 font-medium">
          Öffnet das Teilen-Menü – wähle dort Instagram → Story.
        </div>
        {shareMsg && (
          <div
            className="text-[12px] text-[var(--grau)] font-medium -mt-1"
            data-testid="share-msg"
          >
            {shareMsg}
          </div>
        )}
        <button
          type="button"
          onClick={onRestart}
          data-testid="play-again-button"
          className="kh-btn kh-btn-ghost text-base px-8 py-4 rounded-full w-full"
        >
          Nochmal spielen
        </button>
        <a
          href={SCHNUPPER_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="schnupper-link"
          className="kh-btn kh-btn-ghost text-base px-8 py-4 rounded-full w-full flex items-center justify-center"
        >
          Kennenlerntag am 27.9.: Jetzt mitmachen!
        </a>
      </div>

      <Leaderboard
        entries={leaderboard}
        highlightIndex={highlightIndex}
        className="mt-8 w-full max-w-[320px]"
      />
    </div>
  );
}

const DISPLAY_FONT = "'KH Herz Grotesk', 'Archivo', sans-serif";

function ShareCardArt({ result, innerRef }) {
  const { score, belt, name, level } = result;
  return (
    <div
      ref={innerRef}
      style={{
        width: 360,
        height: 640,
        background: "#fef7f7",
        boxSizing: "border-box",
        position: "relative",
        overflow: "hidden",
        fontFamily: "'Open Sans', sans-serif",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{ height: 10, width: "100%", background: "#e30620" }} />
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "26px 28px 24px",
        }}
      >
        {/* Kopf */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <img
            src="/logo.png"
            alt=""
            width={40}
            height={40}
            style={{ objectFit: "contain" }}
          />
          <div style={{ textAlign: "left", lineHeight: 1.05 }}>
            <div
              style={{
                fontFamily: DISPLAY_FONT,
                textTransform: "uppercase",
                fontWeight: 800,
                fontSize: 14,
                letterSpacing: "0.04em",
                color: "#111318",
              }}
            >
              KAMPFKUNST HERZ®
            </div>
            <div style={{ fontSize: 9.5, color: "#6e6e73", fontWeight: 600 }}>
              Die Lebensschule für Kinder
            </div>
          </div>
        </div>

        {/* Titel */}
        <div
          style={{
            marginTop: 22,
            fontFamily: DISPLAY_FONT,
            textTransform: "uppercase",
            fontWeight: 900,
            fontSize: 30,
            lineHeight: 0.96,
            color: "#e30620",
            textAlign: "center",
          }}
        >
          Zieh durch!
          <br />
          Drück drauf!
        </div>

        {/* Ergebnis-Karte */}
        <div
          style={{
            marginTop: 22,
            width: "100%",
            background: "#fffbfa",
            border: "1.5px solid #ebd7d3",
            borderRadius: 22,
            padding: "22px 20px 24px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            boxShadow: "0 16px 32px -20px rgba(59,10,18,0.4)",
          }}
        >
          <div
            style={{
              fontFamily: DISPLAY_FONT,
              textTransform: "uppercase",
              letterSpacing: "0.18em",
              fontSize: 11,
              fontWeight: 700,
              color: "#6e6e73",
            }}
          >
            {name} hat geschafft
          </div>
          <div
            style={{
              fontFamily: DISPLAY_FONT,
              fontWeight: 900,
              fontSize: 82,
              lineHeight: 1.08,
              color: "#e30620",
              marginTop: 8,
            }}
          >
            {score}
          </div>
          <div
            style={{
              fontFamily: DISPLAY_FONT,
              textTransform: "uppercase",
              letterSpacing: "0.16em",
              fontSize: 12,
              fontWeight: 700,
              color: "#6e6e73",
              marginTop: 2,
            }}
          >
            Punkte
          </div>

          <div
            style={{
              height: 1,
              width: "78%",
              background: "#ebd7d3",
              margin: "18px 0",
            }}
          />

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            <span
              style={{
                background: belt.color,
                color: belt.textColor,
                borderRadius: 9,
                padding: "7px 16px",
                fontFamily: DISPLAY_FONT,
                textTransform: "uppercase",
                fontWeight: 800,
                fontSize: 15,
                boxShadow: `inset 0 0 0 2.5px ${belt.ring}`,
              }}
            >
              Gürtel {belt.label}
            </span>
            <span
              style={{
                background: "#f5ebdd",
                color: "#111318",
                borderRadius: 9,
                padding: "7px 14px",
                fontFamily: DISPLAY_FONT,
                textTransform: "uppercase",
                fontWeight: 700,
                fontSize: 13,
              }}
            >
              Level {LEVELS[level] ? LEVELS[level].label : ""}
            </span>
          </div>
        </div>

        {/* Fuß */}
        <div style={{ marginTop: "auto", textAlign: "center" }}>
          <div
            style={{
              fontFamily: DISPLAY_FONT,
              textTransform: "uppercase",
              fontSize: 15,
              fontWeight: 800,
              color: "#e30620",
            }}
          >
            Spielst du mit?
          </div>
          <div
            style={{
              fontFamily: DISPLAY_FONT,
              textTransform: "uppercase",
              letterSpacing: "0.26em",
              fontSize: 10,
              color: "#6e6e73",
              marginTop: 6,
              fontWeight: 700,
            }}
          >
            Lernen · Lieben · Leben
          </div>
        </div>
      </div>
    </div>
  );
}

const ShareCard = forwardRef(function ShareCard({ result }, ref) {
  return (
    <div
      style={{ position: "fixed", left: -10000, top: 0, pointerEvents: "none" }}
      aria-hidden="true"
    >
      <ShareCardArt result={result} innerRef={ref} />
    </div>
  );
});

function BeltBadge({ belt }) {
  return (
    <div className="flex flex-col items-center gap-2" data-testid="belt">
      <div className="kh-eyebrow">Dein Rang</div>
      <div
        className="relative w-52 h-14 rounded-md flex items-center justify-center"
        style={{
          background: belt.color,
          boxShadow: `inset 0 0 0 3px ${belt.ring}, 0 12px 26px -12px rgba(59,10,18,0.5)`,
        }}
      >
        {/* Gürtelknoten */}
        <div
          className="absolute left-1/2 -translate-x-1/2 w-9 h-16 rounded-sm"
          style={{
            background: belt.color,
            boxShadow: `inset 0 0 0 3px ${belt.ring}`,
          }}
        />
        <span
          className="kh-display text-2xl relative z-10"
          style={{ color: belt.textColor }}
          data-testid="belt-label"
        >
          {belt.label}
        </span>
      </div>
      <div className="text-[11px] text-[var(--grau)] font-semibold tracking-wide">
        Gürtel
      </div>
    </div>
  );
}

function Footer() {
  return (
    <div className="mt-auto pt-6 text-center">
      <div
        className="kh-display text-[11px] text-[var(--grau)]"
        style={{ letterSpacing: "0.28em" }}
      >
        Lernen · Lieben · Leben
      </div>
    </div>
  );
}
